"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import SvgIcon from '@components/shared/svg-icons/SvgIcon'
import useT from '@hooks/useTranslation'

import ComboboxInput from '@components/form/app-input/combobox/ComboboxInput'
import { findMatch, normalize } from '@components/form/app-input/combobox/normalize'
import { navigateHomeSearch } from '@components/form/actionsHome'
import { sellLinks } from '@components/search/formLinks'
import { MENU_ID } from '@components/shared/header/MenuControl'

import prices from '@constants/hints/prices'

import styles from './FrontPageSearchForm.module.css'

// Te same marki i kolejność co sekcja „Popularne marki"; w grupie „Popularne"
// pokazujemy tylko te, które są na pobranej liście (spójność z walidacją).
const POPULAR = ['Audi', 'BMW', 'Ford', 'Mercedes-Benz', 'Opel', 'Honda', 'Renault', 'Skoda', 'Kia', 'Toyota', 'Dacia', 'Volkswagen']

const HOST = process.env.NEXT_PUBLIC_HOST
const FORM_ID = 'home-search'

// Linki do list kategorii (te same adresy co w menu serwisu). Pola wyszukiwarki
// dotyczą wyłącznie osobowych, dlatego ta kategoria jest wyróżniona.
const CATEGORY_ORDER = ['Osobowe', 'Dostawcze', 'Części', 'Ciężarowe', 'Motocykle']
const CURRENT_CATEGORY = 'Osobowe'
const categoryLinks = CATEGORY_ORDER
    .map(t => sellLinks.find(link => link.t === t))
    .filter(Boolean)
    .map(link => ({ t: link.t, to: HOST + link.to }))

const YEAR_MIN = 1990
const fmt = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

const asOptions = (arr) => (arr || []).map((m) => ({ value: m, label: m }))
const fetchHints = (url) => fetch(url).then((r) => { if (!r.ok) throw new Error('net'); return r.json() })

// children: dodatkowa treść na dole karty wyszukiwarki (pasek „Sprzedajesz auto?” od 1024 px).
function FrontPageSearchForm ({ children }) {
    const t = {
        kategorie: useT('Kategorie ogłoszeń'),
        wyszukiwarka: useT('Wyszukiwarka samochodów osobowych'),
        marka: useT('Marka'),
        model: useT('Model'),
        cena: useT('Cena do'),
        rocznik: useT('Rocznik od'),
        marki: useT('Marki'),
        modele: useT('Modele'),
        wiecej: useT('Więcej'),
        wiecejFiltrow: useT('Więcej filtrów'),
        pokaz: useT('Pokaż ogłoszenia'),
    }

    const YEAR_MAX = useMemo(() => new Date().getFullYear() + 1, [])
    const years = useMemo(() => Array.from({ length: YEAR_MAX - YEAR_MIN + 1 }, (_, i) => YEAR_MAX - i), [YEAR_MAX])

    const [make, setMake] = useState('')
    const [mounted, setMounted] = useState(false)   // model aktywny bez JS, zarządzany po hydratacji
    const [menuOpen, setMenuOpen] = useState(false)

    const makeApi = useRef(null), modelApi = useRef(null), priceApi = useRef(null), yearApi = useRef(null)
    const moreBtnRef = useRef(null)
    const liveRef = useRef(null), liveTimer = useRef(null)

    useEffect(() => setMounted(true), [])

    // Stan menu rozgłaszany przez MenuProvider (przycisk „Więcej" jest poza jego drzewem).
    useEffect(() => {
        const onState = (e) => setMenuOpen(Boolean(e.detail?.open))
        window.addEventListener('spl:menu-state', onState)
        return () => window.removeEventListener('spl:menu-state', onState)
    }, [])

    // Jeden region role="status" na formularz; komunikaty z opóźnieniem.
    const announce = useCallback((text, delay = 0) => {
        clearTimeout(liveTimer.current)
        liveTimer.current = setTimeout(() => {
            const el = liveRef.current
            if (!el) return
            el.textContent = ''
            setTimeout(() => { if (liveRef.current) liveRef.current.textContent = text }, 40)
        }, delay)
    }, [])

    const makeSource = useCallback(() => fetchHints('/api/hints/make/car').then(asOptions), [])
    const makeGroups = useCallback((q, items) => {
        if (!q) {
            const popular = POPULAR
                .map((name) => items.find((it) => normalize(it.value) === normalize(name)))
                .filter(Boolean)
            return [
                { label: 'Popularne', items: popular },
                { label: 'Wszystkie marki A–Z', items },
            ]
        }
        const hits = items.map((it) => ({ it, m: findMatch(it.label, q) })).filter((x) => x.m)
        hits.sort((a, b) => a.m.rank - b.m.rank || a.it.label.localeCompare(b.it.label, 'pl'))
        return [{ items: hits.map((x) => x.it) }]
    }, [])

    const modelSource = useCallback(() => make
        ? fetchHints(`/api/hints/model/car?marka=${encodeURIComponent(make)}`).then(asOptions)
        : Promise.resolve([]), [make])
    const modelGroups = useCallback((q, items) => {
        const hits = items.map((it) => ({ it, m: findMatch(it.label, q) })).filter((x) => x.m)
        if (q) hits.sort((a, b) => a.m.rank - b.m.rank)
        return [{ items: hits.map((x) => x.it) }]
    }, [])

    const priceSource = useCallback(() => prices, [])
    const priceGroups = useCallback((q) => {
        const vs = prices.filter((v) => !q || String(v).indexOf(q) === 0)
        return [{ items: vs.map((v) => ({ value: v, label: `${fmt(v)} zł` })) }]
    }, [])
    const priceHlLen = useCallback((it, q) => {
        const s = fmt(it.value)
        let c = 0, i = 0
        while (i < s.length && c < q.length) { if (/\d/.test(s.charAt(i))) c++; i++ }
        return i
    }, [])

    const yearSource = useCallback(() => years, [years])
    const yearGroups = useCallback((q) => {
        const vs = years.filter((v) => !q || String(v).indexOf(q) === 0)
        return [{ items: vs.map((v) => ({ value: v, label: String(v) })) }]
    }, [years])
    const yearValidate = useCallback((v) => (v < YEAR_MIN || v > YEAR_MAX) ? `Rok od ${YEAR_MIN} do ${YEAR_MAX}.` : '', [YEAR_MAX])

    const onMakeCommit = useCallback((v) => { setMake(v); modelApi.current?.clear() }, [])

    const onSubmit = (e) => {
        const bad = [makeApi, modelApi, priceApi, yearApi].find((api) => api.current && !api.current.commit())
        if (bad) { e.preventDefault(); bad.current.focus() }
    }

    // Listing czyta „filtry" z localStorage przy montowaniu (SearchFormContainer),
    // więc zapis musi być synchroniczny — przed nawigacją z akcji serwerowej.
    const openMoreFilters = () => { try { localStorage.setItem('filtry', '1') } catch (err) { /* prywatny tryb: pomiń */ } }

    const onMore = () => {
        setMenuOpen(true)
        window.dispatchEvent(new CustomEvent('spl:menu-toggle', { detail: { opener: moreBtnRef.current } }))
    }

    return (
        <div className={styles.finder}>
            <div className={styles.search}>
                <div className={styles.top}>
                    <nav aria-label={t.kategorie} className={styles.catNav}>
                        <ul className={styles.tabs}>
                            { categoryLinks.map(c => (
                                <li key={c.t}>
                                    <a className={c.t === CURRENT_CATEGORY ? `${styles.tab} ${styles.tabCurrent}` : styles.tab} href={c.to}>{c.t}</a>
                                </li>
                            )) }
                            <li>
                                <button aria-controls={MENU_ID} aria-expanded={menuOpen} className={styles.tab} data-menu-toggle="" onClick={onMore} ref={moreBtnRef} type="button">
                                    {t.wiecej}<SvgIcon name="down" size="sm" />
                                </button>
                            </li>
                        </ul>
                    </nav>
                    <button className={`${styles.ghost} ${styles.moreDesk}`} form={FORM_ID} name="$filtry" onClick={openMoreFilters} type="submit" value="1">
                        <SvgIcon name="sliders" />{t.wiecejFiltrow}
                    </button>
                </div>

                <form action={navigateHomeSearch} aria-label={t.wyszukiwarka} data-home-search id={FORM_ID} onSubmit={onSubmit} role="search">
                    <div className={styles.fields}>
                        <ComboboxInput announce={announce} apiRef={makeApi} errorText="Nie udało się wczytać listy marek." groups={makeGroups}
                            emptyHtml={(v) => `<strong>Brak marki „${v}”</strong>Sprawdź pisownię albo usuń część tekstu, np. „merc” zamiast „mercedes benz”.`}
                            invalidText={(v) => `Brak marki „${v}”.`}
                            label={t.marka} listLabel={t.marki} loadingText="Wczytywanie listy marek." name="marka"
                            onCommit={onMakeCommit} placeholder="Dowolna" source={makeSource} />

                        <ComboboxInput announce={announce} apiRef={modelApi} disabled={mounted && !make}
                            disabledReason="Pole nieaktywne. Najpierw wybierz markę." errorText="Nie udało się wczytać listy modeli." groups={modelGroups}
                            emptyHtml={(v) => `<strong>Brak modelu „${v}”</strong>Ta marka nie ma takiego modelu w ogłoszeniach. Sprawdź pisownię albo wybierz z listy.`}
                            invalidText={(v) => `Brak modelu „${v}”.`}
                            label={t.model} listLabel={t.modele} loadingText="Wczytywanie listy modeli." name="model"
                            placeholder="Dowolny" resetKey={make} source={modelSource} />

                        <ComboboxInput announce={announce} apiRef={priceApi} emptyHtml={() => '<strong>Brak podpowiedzi</strong>Możesz wpisać dowolną kwotę w złotych, np. 37500.'}
                            errorText="Nie udało się wczytać podpowiedzi." groups={priceGroups} hlLen={priceHlLen} label={t.cena} listLabel={t.cena}
                            maxLength={8} name="cena-do" numeric placeholder="Dowolna" source={priceSource} />

                        <ComboboxInput announce={announce} apiRef={yearApi} emptyHtml={() => `<strong>Brak podpowiedzi</strong>Wpisz rok od ${YEAR_MIN} do ${YEAR_MAX}.`}
                            errorText="Nie udało się wczytać podpowiedzi." groups={yearGroups} label={t.rocznik} listLabel={t.rocznik}
                            maxLength={4} name="rocznik-od" numeric placeholder="Dowolny" source={yearSource} validate={yearValidate} />

                        <div className={styles.submitWrap}>
                            <button className={styles.submit} type="submit">
                                <SvgIcon name="search" /><span>{t.pokaz}</span>
                            </button>
                        </div>
                    </div>

                    <button className={`${styles.ghost} ${styles.moreMob}`} name="$filtry" onClick={openMoreFilters} type="submit" value="1">
                        <SvgIcon name="sliders" />{t.wiecejFiltrow}
                    </button>

                    <p aria-live="polite" className={styles.srOnly} ref={liveRef} role="status" />
                </form>
            </div>
            {children}
        </div>
    )
}

export {
    FrontPageSearchForm,
}
